import {useState} from "react";

import {DeleteActionDialog} from "@/components/deleteActionDialog/DeleteActionDialog.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {DropdownHeader} from "@/components/dropdown/subComponents/DropdownHeader/DropdownHeader.tsx";
import {DropdownItem} from "@/components/dropdown/subComponents/DropdownItem/DropdownItem.tsx";
import {DropdownItemText} from "@/components/dropdown/subComponents/DropdownItemText/DropdownItemText.tsx";
import {DropdownButton} from "@/components/dropdownButton/DropdownButton.tsx";
import {MetricsExplorerView} from "@/lib/types/metricsExplorer.ts";
import {ExplorerConfig} from "@/pages/metricsExplorer/explorerConfig.ts";

import styles from "./ChartNameMenu.module.scss";

interface ChartNameMenuProps {
  views: MetricsExplorerView[];
  activeView: MetricsExplorerView | null;
  configOf: (view: MetricsExplorerView) => ExplorerConfig | null;
  onOpen: (view: MetricsExplorerView) => void;
  onNew: () => void;
  onRename: () => void;
  onDelete: () => Promise<void>;
}

/** The chart's name, doubling as the switcher between saved charts, like the dashboard menu. */
export const ChartNameMenu = ({views, activeView, configOf, onOpen, onNew, onRename, onDelete}: ChartNameMenuProps) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  // Renaming rewrites the stored config, so a chart this page can no longer read cannot be renamed.
  const canRename = activeView !== null && configOf(activeView) !== null;

  return (
    <>
      <DropdownButton title={activeView?.name ?? "Untitled chart"} variant="ghost" className={styles.menu}>
        <DropdownItem onClick={onNew}>New Chart</DropdownItem>
        <DropdownItem onClick={onRename} disabled={!canRename}>
          Rename
        </DropdownItem>
        <DropdownItem onClick={() => setShowDeleteConfirmation(true)} disabled={!activeView}>
          Delete
        </DropdownItem>
        <Divider />
        <DropdownHeader>Saved Charts</DropdownHeader>
        {views.length === 0 && <DropdownItemText>No saved charts yet</DropdownItemText>}
        {views.map((view) => (
          <DropdownItem key={view.id} active={view.id === activeView?.id} onClick={() => onOpen(view)}>
            {/* A chart saved before a config change may no longer parse. Marking it beats opening a
                blank explorer and leaving the user to work out why. */}
            {configOf(view) ? view.name : `${view.name} (unreadable)`}
          </DropdownItem>
        ))}
      </DropdownButton>
      <DeleteActionDialog
        show={showDeleteConfirmation}
        title="Delete Chart"
        onCancel={() => setShowDeleteConfirmation(false)}
        onConfirm={async () => {
          await onDelete();
          setShowDeleteConfirmation(false);
        }}
      >
        Are you sure you want to delete "{activeView?.name}"?
      </DeleteActionDialog>
    </>
  );
};
